import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import PhoneInput from 'react-phone-number-input';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import validator from 'validator';
import { putRequest } from '../../helpers/apiHandler';
import { getTimezone } from '../../helpers/helperFunction';
import CloseIcon from '../../layouts/SVG/CloseIcon';

const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const EditUserModal = ({ show, onClose, row, setAlertState, refreshData, countryOptions }) => {
  const user_country = { value: null, label: null };
  const { access_token } = useSelector((state) => state.auth.user);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selected_country, setSelected_country] = useState(user_country);
  const [timezone, setTimezone] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [newPhone, setNewPhone] = useState('');
  const [is_deleted, setIsDeleted] = useState('false');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleNewPhoneChange = (num) => {
    setNewPhone(num);
  };

  const handleSetIsDeleted = () => {
    setIsDeleted((prevValue) => !prevValue);
  };

  const handleSubmit = () => {
    if (!name || validator.isEmpty(name)) {
      setAlertState({
        message: 'Please enter user name',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!validator.matches(name, /^[a-zA-Z ]+$/)) {
      setAlertState({
        message: 'User name must not contain numbers.',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!email || validator.isEmpty(email)) {
      setAlertState({
        message: 'Please enter user email address',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!validator.isEmail(email)) {
      setAlertState({
        message: 'Please enter valid email address',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!selected_country || !selected_country.value || validator.isEmpty(selected_country.value)) {
      setAlertState({
        message: 'Please select the country',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!timezone || !timezone.value || validator.isEmpty(timezone.value)) {
      setAlertState({
        message: 'Please select the timezone',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (!currency || validator.isEmpty(currency)) {
      setAlertState({
        message: 'Please select the timezone',
        show: true,
        variant: 'danger',
      });
      return;
    }

    if (password !== '') {
      if ( white_label_perkeso && !validator.matches(
        password,
        /(?=[A-Za-z0-9@#$%^&+!=]+$)^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@#$%^&+!=]).*$/g))
      {
        setAlertState({
          message:
            'Please choose a strong password that includes at least 1 lowercase and uppercase letter, a number, as well as a special character (!@#$%^&*)',
          show: true,
          variant: 'danger',
        });
        return;
      }

      const min_pwd_length = white_label_perkeso ? 12 : 8;
      if (!validator.isLength(password, { min: min_pwd_length, max: undefined })) {
        setAlertState({
          message: `The password must be at least ${min_pwd_length} characters long.`,
          show: true,
          variant: 'danger',
        });
        return;
      }

      if (!confirmPassword || validator.isEmpty(confirmPassword)) {
        setAlertState({
          message: 'Please confirm your password',
          show: true,
          variant: 'danger',
        });
        return;
      }

      if (password !== confirmPassword) {
        setAlertState({
          message: 'Password do not match',
          show: true,
          variant: 'danger',
        });
        return;
      }
    }
    updateUser();
  };

  const updateUser = () => {
    const body = {
      name,
      email,
      phone: newPhone || null,
      country: selected_country,
      timezone,
      currency,
      is_deleted,
      password: password,
    };
    putRequest(`/admin/user-management/` + row.id, body, access_token.token, (res) => {
      if (res.success) {
        refreshData();
        onClose();
        setAlertState({
          message: 'User profile successfully updated.',
          show: true,
          variant: 'success',
        });
      } else {
        if (res.code === 1037) {
          setAlertState({
            message: res.data.email + ' already exist',
            show: true,
            variant: 'warning',
          });
          return;
        }
        setAlertState({
          message:
            'Unable to update user. Please try again or contact support if the issue persist',
          show: true,
          variant: 'danger',
        });
      }
    });
  };

  useEffect(() => {
    if (row.id) {
      setName(row.name);
      setEmail(row.email);
      setSelected_country({ value: row.country, label: row.country });
      const tz = getTimezone().find((x) => x.value === row.timezone);
      setTimezone(tz);
      if (row.currency) {
        setCurrency(row.currency.iso3);
      }
      setIsDeleted(row.is_deleted);
      handleNewPhoneChange(row.phone_number);
    }
  }, [row]);

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Body>
        <CloseIcon handleModalClose={onClose} />
        <Modal.Title as="h4" style={{ margin: '8px 0 16px' }}>Edit User</Modal.Title>
        <Form>
          <Container>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>User ID</Form.Label>
                  <Form.Control type="input" value={row.id} disabled={true} />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="input"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group>
                  <Form.Label>Mobile Number</Form.Label>
                  <PhoneInput
                    className="form-control"
                    international
                    defaultCountry="MY"
                    value={newPhone}
                    onChange={handleNewPhoneChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Country</Form.Label>
                  <Select
                    name="country"
                    options={countryOptions}
                    value={selected_country}
                    onChange={setSelected_country}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Time Zone</Form.Label>
                  <Select
                    name="timezone"
                    options={getTimezone()}
                    value={timezone}
                    onChange={(value) => setTimezone(value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              {!white_label_perkeso && (
                <Col sm={6}>
                  <Form.Group>
                    <Form.Label>Default currency</Form.Label>
                    <Form.Control
                      as="select"
                      name="currency"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                    >
                      <option value="USD">United States Dollars</option>
                      <option value="EUR">Euro</option>
                      <option value="MYR">Malaysia Ringgit</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
              )}
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>User Account Status</Form.Label>
                  <Form.Control
                    as="select"
                    name="account_status"
                    value={is_deleted}
                    onChange={(e) => handleSetIsDeleted(e.target.value)}
                  >
                    <option value="true">SUSPENDED</option>
                    <option value="false">ACTIVE</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              { white_label_perkeso && (
                <>
                <Col sm={6}>
                  <Form.Group>
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
            <Row>
              <Col>
                <Form.Group className="d-flex justify-content-start" style={{ marginTop: 24, gap: 16 }}>
                  <Button variant="primary" onClick={handleSubmit} style={{ width: "140px" }}>
                    {'Save'}
                  </Button>
                  <Button variant="ghost" onClick={onClose} style={{ width: "140px" }}>
                    Cancel
                  </Button>
                </Form.Group>
              </Col>
            </Row>
          </Container>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditUserModal;
